import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import DictTracker from "../../../../prop-trackers/dict-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import Zipper from "../../../../prop-tracker-utils/zipper.js";
import { ForEach, ValueTracker as ForEachValueTracker } from "../../../../prop-tracker-utils/for-each.js";
import SinglePropDeepTracker from "../../../../prop-tracker-utils/single-prop-deep-tracker.js";

class PlacementTrackerController {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, placementController) {
        this._placementController = placementController;

        this._coordTracker = new PropTracker();
        this._objectTracker = new PropTracker();

        this._zipper = new Zipper();
        this._zipper.setSource("coords", this._coordTracker);
        this._zipper.setSource("object", this._objectTracker);

        this._pairTracker = new DictTracker();
        this._forEachTracker = new ForEach(this._pairTracker, (key) => new ObjectTracker(evtCtx, placementController));
    }

    trackData(objectCoords, objectCoordEvents) {
        this._coordTracker.track(objectCoords, objectCoordEvents);
    }

    trackObjects(objects, objectEvents) {
        this._objectTracker.track(objects, objectEvents);
    }

    update() {
        this._zipper.update(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: key, props }) => {
                    const { newValue: coords } = props.get("coords");
                    const { newValue: object } = props.get("object");

                    if (coords && object) {
                        this._pairTracker.setValue(key, { coords, object });
                    } else {
                        this._pairTracker.deleteValue(key);
                    }
                });
            }
        });

        this._forEachTracker.update(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update();
            }
        });
    }
}

const Params = ["position", "rotation", "quaternion", "scale"];

class ObjectTracker extends ForEachValueTracker {
    constructor(evtCtx, placementController) {
        super();

        this._evtCtx = evtCtx;
        this._placementController = placementController;

        const needsUpdate = () => this.protected_needsUpdate();

        this._objectTracker = new ValueTracker();
        this._coordsTracker = new ValueTracker();

        this._objectTracker.listenNeedsUpdate(needsUpdate);
        this._coordsTracker.listenNeedsUpdate(needsUpdate);

        this._coordTrackers = {};

        for (const param of Params) {
            const tracker = new SinglePropDeepTracker(evtCtx);
            tracker.listenNeedsUpdate(needsUpdate);
            this._coordTrackers[param] = tracker;
        }

        this._object = null;
        this._coords = null;
    }

    setValue(data) {
        if (data) {
            this._objectTracker.setValue(data.object);
            this._coordsTracker.setValue(data.coords);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._objectTracker.deleteValue();
        this._coordsTracker.deleteValue();
    }

    protected_startUpdate() {
        this._coordsTracker.updateIfNeeded(({ prop: { newValue: coords } }) => {
            this._coords = coords || null;
            const events = coords ? this._evtCtx.getEvents(coords) : undefined;

            for (const param of Params) {
                this._coordTrackers[param].track(coords, events, param);
            }
        });

        const updateObject = this._objectTracker.needsUpdate;
        if (updateObject) {
            this._objectTracker.update(({ prop: { newValue: object } }) => {
                this._object = object || null;
            });
        }

        const object = this._object, coords = this._coords;

        if (object && coords) {
            const {
                position: posTracker,
                rotation: rotTracker,
                quaternion: quatTracker,
                scale: scaleTracker,
            } = this._coordTrackers;

            const { position, rotation, quaternion, scale } = coords;

            if (updateObject || posTracker.needsUpdate) {
                posTracker.update();

                if (position) {
                    const { x, y, z } = position;
                    this._placementController.setPosition(object, x, y, z);
                }
            }

            if (updateObject || rotTracker.needsUpdate) {
                rotTracker.update();

                if (rotation) {
                    const { x, y, z, order } = rotation;
                    this._placementController.setEulerRotation(object, x, y, z, order);
                }
            }

            if (updateObject || quatTracker.needsUpdate) {
                quatTracker.update();

                if (quaternion) {
                    const { x, y, z, w } = quaternion;
                    this._placementController.setQuaternionRotation(object, x, y, z, w);
                }
            }

            if (updateObject || scaleTracker.needsUpdate) {
                scaleTracker.update();

                if (scale) {
                    const { x, y, z } = scale;
                    this._placementController.setScale(object, x, y, z);
                }
            }
        }
    }

    protected_finishUpdate() {}
}

export default PlacementTrackerController;
